import React, { useState } from 'react'

import { faExternalLink } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Link, TextField, Typography } from '@material-ui/core'
import PropTypes from 'prop-types'

import api from 'common/api'
import useScid from 'common/use-scid'

import StepHeader from './StepHeader'

const EnableAudienceInsights = ({ config, getConfig }) => {
  const [queryConfig, setQueryConfig] = useState({
    slateMatchResultQueryUrl: '',
  })
  const [error, setError] = useState(false)
  const scid = useScid()
  const activateMatchResults = () => {
    api
      .post(`/api/colleges/${scid}/integration/crm/configuration/activate-match-results`, queryConfig)
      .then(() => {
        getConfig()
        setError(false)
      })
      .catch(() => setError(true))
  }
  const messagingActivated = config && config.messagingActivated
  const matchingActivated = config && config.matchingActivated
  const matchResultsActivated = config && config.matchResultsActivated
  const activatingMatchResults = messagingActivated && matchingActivated && !matchResultsActivated

  return activatingMatchResults ? (
    <>
      <hr />
      <StepHeader
        number={3}
        text="Share Match Results"
        isActive
      />
      <Box mt={4}>
        <Typography
          variant="h6"
          component="h3"
        >
          Unlock your Scoir follower insights report
        </Typography>
      </Box>
      <Typography
        variant="body2"
        color="textSecondary"
      >
        When you share back your match results, we'll return a breakdown of your stealth and known followers by high
        school graduation year. We'll also look at your followers' date of record creation in Slate against their date
        of first follow in Scoir so you can accurately attribute first source of origin. These insights are a powerful
        tool to ensure you're getting in front of your followers early and that you have all the details to best measure
        your vendor ROI.
      </Typography>
      <Box py={3}>
        <Button
          variant="outlined"
          size="large"
          color="primary"
          component="a"
          href="https://scoir.helpdocs.io/article/hxl1tybntt-for-colleges-slate-integration-message-scoir-students-from-slate#unlocking_stealth_insights"
          rel="noopener noreferrer"
          target="_blank"
        >
          Read Help Article&nbsp;&nbsp;
          <FontAwesomeIcon icon={faExternalLink} />
        </Button>
      </Box>
      <Typography>In Slate, copy this information and paste below:</Typography>
      <Box
        mt={3}
        mb={3}
      >
        <TextField
          variant="outlined"
          type="text"
          label="Query String"
          data-test-id="queryString"
          placeholder="Query String"
          required
          fullWidth
          value={queryConfig.slateMatchResultQueryUrl}
          onChange={(e) => {
            const value = e.target.value
            setQueryConfig((prev) => ({ ...prev, slateMatchResultQueryUrl: value }))
          }}
        />
      </Box>
      <Button
        color="primary"
        size="large"
        variant="contained"
        onClick={activateMatchResults}
        disabled={!queryConfig.slateMatchResultQueryUrl}
      >
        Unlock Stealth Insights
      </Button>
      {error && (
        <Box mt={2}>
          <Typography
            variant="caption"
            color="error"
          >
            Something went wrong.{' '}
            <Link
              href="https://www.scoir.com/support"
              target="_blank"
              rel="noreferrer"
            >
              Please contact customer support.
            </Link>
          </Typography>
        </Box>
      )}
      <Box mt={3}>
        <Typography
          variant="caption"
          color="textSecondary"
        >
          *Stealth records are interested Scoir students, or inquiries, not yet recorded in your CRM. These students can
          only be reached and unlocked by selecting Scoir as your mailing channel in Slate, or directly from your Scoir
          account. Scoir does not share the personally identifiable information of stealth students.
        </Typography>
      </Box>
    </>
  ) : (
    <>
      <hr />
      <StepHeader
        number={3}
        text="Share Match Results"
        isActive={messagingActivated && matchingActivated}
        isComplete={matchResultsActivated}
      />
    </>
  )
}

EnableAudienceInsights.propTypes = {
  config: PropTypes.object,
  getConfig: PropTypes.func.isRequired,
}

export default EnableAudienceInsights
